/**
 * CompactAnnotationList — die Anmerkungen unter einem eingebetteten
 * Entwurf, je Anmerkung eine Zeile (#2574, Post 5623, Alex' drittes Video).
 *
 * Statt der vollen Karte (Kopf, Zitat, Notiz, „Antworten“, Faden) steht
 * hier nur: Notiz, Zustand und die letzte Antwort („CloudV2: Entfernt.“).
 * Geantwortet wird über eine neue Anmerkung, nicht im Faden.
 *
 * Zustände:
 *  - offen
 *  - eingearbeitet: Section-Status `resolved`, ausgegraut
 *  - Stelle entfernt: Der verankerte Absatz und das Zitat sind nicht mehr
 *    im Text, etwa weil der Agent den Satz gelöscht hat.
 */

import type { AnnotationData } from './AnnotationHighlight'

interface StructuredParagraph {
  text: string
  block_id?: string | null
  section_id?: string | null
  section_type?: string | null
  section_role?: string | null
  section_attrs?: Record<string, unknown> | null
}

interface Props {
  paragraphs: StructuredParagraph[]
  annotations: AnnotationData[]
}

type Zustand = 'offen' | 'eingearbeitet' | 'stelle-entfernt'

interface Row {
  id: string
  note: string
  zustand: Zustand
  reply: { by: string; text: string } | null
}

const ZUSTAND_TEXT: Record<Zustand, string> = {
  offen: 'offen',
  eingearbeitet: 'eingearbeitet',
  'stelle-entfernt': 'Stelle entfernt',
}

export function buildRows(paragraphs: StructuredParagraph[], annotations: AnnotationData[]): Row[] {
  const body = paragraphs.filter((p) => !p.section_id)
  const bodyBlocks = new Map(body.map((p) => [p.block_id || '', p.text]))
  const bodyText = body.map((p) => p.text).join('\n')
  const rows: Row[] = []
  for (const p of paragraphs) {
    if (p.section_type !== 'annotation' || p.section_role !== 'start' || !p.section_id) continue
    const attrs = (p.section_attrs || {}) as Record<string, unknown>
    const meta = annotations.find((a) => String(a.id) === p.section_id)
    const note = (meta?.note_text || '').trim()
    if (!note) continue // Markierung ohne Notiz: keine Zeile
    const blockId = typeof attrs.anchor_paragraph_id === 'string' ? attrs.anchor_paragraph_id : ''
    const quote = typeof attrs.anchor_quote === 'string' ? attrs.anchor_quote.trim() : ''
    const anchorText = bodyBlocks.get(blockId)
    const stelleDa = quote
      ? (anchorText !== undefined && anchorText.includes(quote)) || bodyText.includes(quote)
      : anchorText !== undefined
    const resolved = String(attrs.status || '').toLowerCase() === 'resolved'
    const last = meta?.replies?.length ? meta.replies[meta.replies.length - 1] : null
    rows.push({
      id: p.section_id,
      note,
      zustand: resolved ? 'eingearbeitet' : stelleDa ? 'offen' : 'stelle-entfernt',
      reply: last ? { by: (last.author_name || '').replace(/^agent:/, ''), text: last.text.trim() } : null,
    })
  }
  return rows
}

export function CompactAnnotationList({ paragraphs, annotations }: Props) {
  const rows = buildRows(paragraphs, annotations)
  if (rows.length === 0) return null
  return (
    <ul className="kompakt-anmerkungen" aria-label="Anmerkungen">
      {rows.map((r) => (
        <li key={r.id} className={`kompakt-anmerkung kompakt-anmerkung--${r.zustand}`} data-section-id={r.id}>
          <span className="kompakt-anmerkung__notiz">{r.note}</span>
          <span className="kompakt-anmerkung__zustand">{ZUSTAND_TEXT[r.zustand]}</span>
          {r.reply && (
            <span className="kompakt-anmerkung__antwort">
              {r.reply.by ? `${r.reply.by}: ` : ''}
              {r.reply.text}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}
